import './reader.css'

import React, { useEffect, useState } from 'react'
import { Services } from '@/services/services'
import { CurrentSession, SessionService } from '@/services/repository/session'
import { useNavigate } from 'react-router-dom'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { Translates } from '@/services/repository/i18n'
import { BackButton } from '../layout/NavBar'
import { Spinner } from '../spinner'

export function isNFCSupported(): boolean {
  return ('NDEFReader' in window)
}

export interface NFCReaderProps {
  onRead: (cardID: string) => void
  onReadError: () => void
  onInit: (success: boolean) => void
}

export const readNFC = (props: NFCReaderProps) => {
  if (!('NDEFReader' in window)) {
    props.onInit(false)
    return () => { }
  }

  function log(msg: string) {
    console.log(msg)
  }

  try {
    const ndef = new NDEFReader()
    const abortController = new AbortController()
    let aborted = false

    const onReadingHandler = (event: any) => {
      if (aborted) return

      const { message, serialNumber } = event as NDEFMessageEvent
      log(`> Serial Number: ${serialNumber}`)
      log(`> Records: (${message.records.length})`)

      if (!serialNumber) {
        props.onReadError()
        return
      }

      const ucid = serialNumber.replaceAll(':', '').toUpperCase()
      props.onRead(ucid)
    }

    ndef
      .scan({ signal: abortController.signal })
      .then(
        () => {
          ndef.addEventListener('reading', onReadingHandler)
          ndef.addEventListener('readingerror', props.onReadError)
          log('nfc scanner: inited')
          props.onInit(true)
        },
        () => {
          props.onInit(false)
          log('nfc scanner: start error')
        }
      )

    log('nfc scanner: started')

    return () => {
      aborted = true
      abortController.abort()
      ndef.removeEventListener('reading', onReadingHandler)
      ndef.removeEventListener('readingerror', props.onReadError)
    }
  } catch (error) {
    props.onInit(false)
    return () => { }
  }
}


export interface NFCReaderPopupProps {
  onRead: (cardID: string) => void
  onClose: () => void
  translates: Translates
}

export enum NFCReaderPopupStatuses {
  error,
  unsupported,
  ready,
  readed
}

export const NFCReaderPopup = (props: NFCReaderPopupProps) => {
  const { translates } = props
  const [cardId, setCardId] = useState<string>()
  const [status, setStatus] = useState<NFCReaderPopupStatuses>()

  useEffect(() => {
    return readNFC({
      onInit: (success: boolean) => {
        setStatus(success ? NFCReaderPopupStatuses.ready : NFCReaderPopupStatuses.unsupported)
      },
      onReadError: () => {
        setStatus(NFCReaderPopupStatuses.error)
      },
      onRead: (newCardId) => {
        setStatus(NFCReaderPopupStatuses.readed)
        setCardId(newCardId)
        props.onRead(newCardId)
        props.onClose()
      }
    })
  }, [])

  if (status === NFCReaderPopupStatuses.error) {
    return (
      <div className="nfcreader-popup">
        <div className='icon-box'>
          <i className='svgfont-nfc' />
        </div>
        <div className="status">{translates.for('elements.nfcreader.status.error')}</div>
      </div>
    )
  }

  if (status === NFCReaderPopupStatuses.readed) {
    return (
      <div className="nfcreader-popup">
        <div className='icon-box'>
          <i className='svgfont-nfc' />
        </div>
        <div className="status">
          {translates
            .for('elements.nfcreader.status.readed')
            .replace('{{card_id}}', cardId ?? '')}
        </div>
      </div>
    )
  }

  if (status === NFCReaderPopupStatuses.ready) {
    return (
      <div className="nfcreader-popup">
        <div className='icon-box'>
          <i className='svgfont-nfc' />
        </div>
        <div className="status">{translates.for('elements.nfcreader.status.insert_nfc_card')}</div>
      </div>
    )
  }

  if (status === NFCReaderPopupStatuses.unsupported) {
    return (
      <div className="nfcreader-popup">
        <div className='icon-box'>
          <i className='svgfont-nfc' />
        </div>
        <div className="status">{translates.for('elements.nfcreader.status.unsupported')}</div>
      </div>
    )
  }

  return (
    <div className="nfcreader-popup">
      <Spinner />
    </div>
  )
}

export const NFCReaderPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const navigate = useNavigate()
  const [logs, setLogs] = useState<string[]>([])

  function log(msg: string) {
    setLogs(oldArray => [...oldArray, msg])
  }

  useEffect(() => {
    try {
      const ndef = new NDEFReader()

      const onReadingHandler = (event: any) => {
        const { message, serialNumber } = event as NDEFMessageEvent
        log(`> Serial Number: ${serialNumber}`)
        log(`> Records: (${message.records.length})`)
      }

      const onReadingErrorHandler = () => {
        log("Argh! Cannot read data from the NFC tag. Try another one?")
      }

      ndef.scan().then(() => {
        ndef.addEventListener("reading", onReadingHandler)
        ndef.addEventListener("readingerror", onReadingErrorHandler)
        log("Started")
      }, () => {
        log("promis error")
      })
      log("Starting")


      return () => {
        ndef.removeEventListener("reading", onReadingHandler)
        ndef.removeEventListener("readingerror", onReadingErrorHandler)
      }
    } catch (error) {
      log("Error: " + error)
      log("Try run the page on android device")
    }
  }, [])

  return <LoggedInPageLayout pageName='nfcreader' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>
    <div className='nfcreader-rows'>
      {logs.map((row) => <div className='row'>{row}</div>)}
    </div>
  </LoggedInPageLayout>
}
